import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient
} from 'react-query';
import {
  positionOpenerAxiosApi,
  predictionManagerAxiosApi
} from 'src/Config/axiosApi';
import { resolveOpenerApi } from './openerEndpoint';
import {
  getOpenerState,
  startOpener,
  stopOpener,
  clearOpenerPanicLockout,
  OpenerState
} from 'src/Api/exchanges';
import {
  EntryRule,
  EntryRuleType,
  EntryExecution,
  CreateEntryRuleDto,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  EntryOrdersResponse,
  MakerFlag
} from '../types/entry.types';
import { recoverFromDuplicateCreate, seedRuleIntoLists } from './ruleCreation';
import { enqueueOpenerWrite } from './requestQueue';

/**
 * Status set the opener page operates on. `expired` is included because
 * prediction-market expiry dates are frequently wrong (an intraday market
 * stored hours early flips to `expired` while still live); the operator must
 * still see and send such a pair to the opener. The bucket page's
 * `splitActiveExpired` routes the expired ones into the Expired bucket.
 */
export const OPENER_PAIR_STATUS = 'active,expired';

/**
 * Debounced reconcile for the `entry-rules` list. Each maker-flag toggle
 * optimistically patches the row switch, then used to fire its OWN full-list
 * refetch on settle. Flipping several legs fast → N refetches racing; a stale
 * in-flight one lands on top of a later optimistic patch and a just-enabled
 * switch snaps back / looks frozen (the symptom behind the bursty-enable
 * lag + errors). Collapsing the post-toggle reconcile into ONE trailing
 * refetch keeps the optimistic UI authoritative through the burst; the 5s
 * `refetchInterval` backstops anything the trailing refetch misses. Shared at
 * module scope so a whole burst of toggles coalesces into a single refetch.
 */
export const ENTRY_RULES_RECONCILE_DEBOUNCE_MS = 600;
let entryRulesReconcileTimer: ReturnType<typeof setTimeout> | null = null;
export function debouncedInvalidateEntryRules(qc: QueryClient): void {
  if (entryRulesReconcileTimer) clearTimeout(entryRulesReconcileTimer);
  entryRulesReconcileTimer = setTimeout(() => {
    entryRulesReconcileTimer = null;
    void qc.invalidateQueries('entry-rules');
  }, ENTRY_RULES_RECONCILE_DEBOUNCE_MS);
}

/**
 * Resolve the opener axios instance a per-rule WRITE should target, given the
 * rule's `_id`. A grouped rule must activate/pause/toggle/maker-flag against
 * its owning group's feed pod (core 409s grouped writes); ungrouped pairs use
 * the core base.
 *
 * The group is read STRAIGHT OFF the rule object in the `entry-rules` cache
 * (`rule.openerGroupId`) — the page's authoritative, always-loaded source.
 * It deliberately NO LONGER cross-joins the volatile `manual-confirmed-pairs`
 * cache: a miss there (in-flight refetch, a pair added to a group after the
 * cache loaded) used to resolve `null` and silently route a grouped write to
 * the CORE pod, which served it 200 and re-armed the rule (spec D4/CR-4).
 *
 * Routing is fail-visible: a grouped rule whose feed base can't be resolved
 * THROWS (`UnresolvedOpenerGroupError`) instead of falling back to core — the
 * caller runs this inside its `mutationFn`, so the throw reaches the mutation
 * `onError` + the app-wide `MutationCache.onError` toast. A rule absent from
 * the cache (`openerGroupId` undefined) is treated as ungrouped → core; the
 * page binds rows to the rule cache, so a write is only issued for a rule the
 * cache holds. The returned instance carries the shared bearer/refresh
 * interceptors (see `axiosApi.ts`).
 */
export function resolveOpenerApiForRule(qc: QueryClient, ruleId: string) {
  let openerGroupId: string | null | undefined;
  for (const [, rules] of qc.getQueriesData<EntryRule[]>('entry-rules')) {
    const rule = rules?.find(r => r._id === ruleId);
    if (rule) {
      openerGroupId = rule.openerGroupId ?? null;
      break;
    }
  }
  return resolveOpenerApi(openerGroupId ?? null);
}

/**
 * Fetch all entry rules, optionally filtered by `ruleType` ('tna' or
 * 'sum_of_sides'). Mirrors `useUnwindingRules` so the page-level slicing
 * patterns (active vs paused, by pairId) carry over unchanged.
 */
export function useEntryRules(opts?: { ruleType?: EntryRuleType }) {
  const ruleType = opts?.ruleType;
  return useQuery<EntryRule[]>(
    ['entry-rules', ruleType ?? 'all'],
    async () => {
      const { data } = await positionOpenerAxiosApi.get<EntryRule[]>(
        '/rules',
        ruleType ? { params: { ruleType } } : undefined
      );
      return data;
    },
    { refetchInterval: 5000, keepPreviousData: true }
  );
}

export function useCreateEntryRule() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateEntryRuleDto) => {
      try {
        const { data } = await positionOpenerAxiosApi.post<EntryRule>(
          '/rules',
          dto
        );
        return data;
      } catch (err) {
        // Duplicate-create conflict (409 + existingRuleId) means the pair
        // already has a rule — resolve to it so the caller proceeds with
        // its PATCH instead of surfacing a failure toast. Anything else
        // rethrows untouched.
        return recoverFromDuplicateCreate(err, async id => {
          const { data } = await positionOpenerAxiosApi.get<EntryRule>(
            `/rules/${id}`
          );
          return data;
        });
      }
    },
    {
      mutationKey: 'entry:create-rule',
      onSuccess: created => {
        // Seed the rule into every cached `entry-rules` list so rows bind
        // to it the moment the create resolves. Invalidation alone left a
        // ~1-5s window where the row still saw `rule === undefined` and a
        // switch click fired a second create for the same pair.
        seedRuleIntoLists(
          qc.getQueriesData<EntryRule[]>('entry-rules'),
          created
        ).forEach(([key, data]) => qc.setQueryData(key, data));
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useUpdateEntryRule() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: UpdateEntryRuleDto }) => {
      const { data } = await enqueueOpenerWrite(() =>
        positionOpenerAxiosApi.patch<EntryRule>(`/rules/${id}`, dto)
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Bulk parameter update across EXISTING entry rules in one request.
 * `scope: 'all'` updates every rule for the given `path`; `scope: 'all'` +
 * `subgroupId` narrows that to one opener subgroup (groupId XOR subgroupId,
 * never both); `scope: 'ids'` updates just the supplied `ruleIds`. Only the
 * filled params
 * (`thresholdSum` / `size` / `maxVolumeContracts` / `makerSizeMode` /
 * `counterStuckMarketHedgeSeconds`) are
 * changed — an absent field is left untouched server-side. Mirrors
 * `useUpdateEntryRule` (same
 * `positionOpenerAxiosApi` + `entry-rules` invalidation); rule-less rows are
 * staged client-side by the caller, not created here.
 */
export function useBulkUpdateEntryRuleParams() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: {
      scope?: 'all' | 'ids';
      ruleIds?: string[];
      subgroupId?: string;
      path: 'a' | 'b';
      thresholdSum?: number;
      size?: number;
      maxVolumeContracts?: number;
      makerSizeMode?: 'dynamic' | 'fixed';
      counterStuckMarketHedgeSeconds?: number;
      disablePlacementGatePolymarket?: boolean;
      disablePlacementGateKalshi?: boolean;
      counterMakerWalkStepSeconds?: number | null;
      counterMakerWalkStepCents?: number | null;
      lockstepMaxAheadBatches?: number | null;
    }) => {
      const { data } = await positionOpenerAxiosApi.patch<{
        scope: string;
        matched: number;
        modified: number;
      }>('/rules/bulk-params', dto);
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/** A single rule that diverged from the representative sample at save time. */
/** POST /rules/groups/:groupId/default-config result. */
export interface SaveGroupDefaultConfigResult {
  saved: true;
  ruleCount: number;
  savedAt: string;
}

/** POST /rules/groups/:groupId/default-config/reset result. */
export interface ResetGroupDefaultConfigResult {
  matched: number;
  modified: number;
  skipped: number;
}

/**
 * Snapshot each member rule's current config as its own per-rule "default" —
 * every rule remembers its OWN params (config-only; never the enable/maker
 * toggles). Re-reads the rules AND the groups list so the freshly stamped
 * `defaultConfigSavedAt` shows up.
 */
export function useSaveGroupDefaultConfig() {
  const qc = useQueryClient();
  return useMutation(
    async (groupId: string) => {
      const { data } =
        await positionOpenerAxiosApi.post<SaveGroupDefaultConfigResult>(
          `/rules/groups/${groupId}/default-config`
        );
      return data;
    },
    {
      mutationKey: 'entry:save-group-default',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
        qc.invalidateQueries(['opener-groups']);
      }
    }
  );
}

/**
 * Re-apply each member rule's OWN saved default config back onto it — config
 * fields only, NEVER the enable/maker toggles. Applies hot (active rules keep
 * running with the restored params). Rules created after the last Save have no
 * snapshot and are skipped. 400s when no rule in the group has a saved default
 * (the caller gates the button on `defaultConfigSavedAt`). Re-reads the rules
 * since the stamp may have changed many of them.
 */
export function useResetGroupDefaultConfig() {
  const qc = useQueryClient();
  return useMutation(
    async (groupId: string) => {
      const { data } =
        await positionOpenerAxiosApi.post<ResetGroupDefaultConfigResult>(
          `/rules/groups/${groupId}/default-config/reset`
        );
      return data;
    },
    {
      mutationKey: 'entry:reset-group-default',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useDeleteEntryRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await positionOpenerAxiosApi.delete(`/rules/${id}`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useTogglePlatform() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      platform,
      enabled
    }: {
      id: string;
      platform: 'kalshi' | 'polymarket';
      enabled: boolean;
    }) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).post(`/rules/${id}/toggle-platform`, {
          platform,
          enabled
        })
      );
      return data;
    },
    {
      mutationKey: 'entry:toggle-platform',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Set one of the four per-(platform, side) maker flags on a rule.
 * Backend's RulesService.update() re-resolves the rule's path and
 * derives kalshiConfig.side / polymarketConfig.side from the merged
 * flag state — the caller only needs to pass the flag it wants to
 * change. The `clearOthers` option lets the caller atomically clear
 * the cross-path flags when switching from path A → path B (avoids
 * a transient cross-path BadRequest from the validator).
 */
export function useSetMakerFlag() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      flag,
      value,
      clearOthers
    }: {
      id: string;
      flag: MakerFlag;
      value: boolean;
      clearOthers?: MakerFlag[];
    }) => {
      const dto: Partial<Record<MakerFlag, boolean>> = { [flag]: value };
      if (clearOthers) {
        for (const f of clearOthers) dto[f] = false;
      }
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).patch<EntryRule>(`/rules/${id}`, dto)
      );
      return data;
    },
    {
      mutationKey: 'entry:set-maker-flag',
      // Optimistically patch the maker flag(s) into every cached
      // `entry-rules` list so the row's switch reflects the click
      // immediately — the switch's `checked` binds to `rule[flag]`, which
      // otherwise only changed after the 5s refetch landed, making the
      // toggle look frozen / "not taken" after turning a rule off.
      onMutate: async ({ id, flag, value, clearOthers }) => {
        await qc.cancelQueries('entry-rules');
        const snapshots = qc.getQueriesData<EntryRule[]>('entry-rules');
        const patch: Partial<Record<MakerFlag, boolean>> = { [flag]: value };
        if (clearOthers) for (const f of clearOthers) patch[f] = false;
        qc.setQueriesData<EntryRule[] | undefined>('entry-rules', old =>
          old?.map(r => (r._id === id ? { ...r, ...patch } : r))
        );
        return { snapshots };
      },
      onError: (_err, _vars, ctx) => {
        // Roll the optimistic patch back to the pre-mutation snapshots.
        ctx?.snapshots?.forEach(([key, data]) =>
          qc.setQueryData(key, data)
        );
      },
      onSettled: () => {
        // Reconcile against server truth — picks up backend side-effects
        // the optimistic patch can't know (e.g. status→paused when the
        // last maker flag goes off, resolved config sides). Debounced so a
        // burst of fast leg toggles collapses into ONE trailing refetch
        // instead of N racing ones that clobber the optimistic switches.
        debouncedInvalidateEntryRules(qc);
      }
    }
  );
}

/**
 * Bulk set one of the four maker flags across many entry rules in one
 * round trip. `scope: 'all'` + `groupId` flips every rule in that opener
 * group; `scope: 'all'` + `subgroupId` flips every rule in that opener
 * subgroup (groupId XOR subgroupId, never both); a bare `scope: 'all'`
 * flips every rule; `scope: 'ids'` flips the given `ruleIds`. Same backend
 * cascade as `useSetMakerFlag` per rule —
 * `value: true` ACTIVATES paused rules; the last-flag-off auto-pauses.
 * Mirrors `useSetMakerFlag` / `useBulkSetPairOpenerEnabled`: same
 * `positionOpenerAxiosApi`, same `entry-rules` invalidation, and the global
 * `MutationCache.onError` backstop surfaces failures via the mutationKey.
 */
export function useBulkSetMakerFlag() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: {
      scope?: 'all' | 'ids';
      ruleIds?: string[];
      groupId?: string;
      subgroupId?: string;
      flag: MakerFlag;
      value: boolean;
    }) => {
      const { data } = await positionOpenerAxiosApi.patch<{
        scope: 'all' | 'ids';
        matched: number;
        modified: number;
        skipped: number;
      }>('/rules/bulk-maker-flag', dto);
      return data;
    },
    {
      mutationKey: 'entry:bulk-set-maker-flag',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Bulk arm/disarm — sets `desiredArmed` across many entry rules in one round
 * trip. `desiredArmed` is now the ONLY arm/disarm trigger (a maker-flag flip no
 * longer arms), so this is the bulk equivalent of the per-row on/off switch.
 * `scope: 'all'` + `groupId` arms/disarms every rule in that opener group;
 * `scope: 'all'` + `subgroupId` every rule in that opener subgroup (groupId XOR
 * subgroupId, never both); a bare `scope: 'all'` every rule; `scope: 'ids'` the
 * given `ruleIds`.
 *
 * Unlike the per-rule `useSetRuleArmed` (which routes each write to its owning
 * feed pod), this stays on the CORE base exactly like `useBulkSetMakerFlag`: the
 * backend `/rules/bulk-armed` endpoint itself fans grouped arming out to the
 * right feed pods, so the FE never does per-group routing for the bulk path.
 * Tagged `entry:bulk-armed` so the app-wide `MutationCache.onError` backstop
 * surfaces a failure with tailored copy.
 */
export function useBulkSetArmed() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: {
      scope?: 'all' | 'ids';
      ruleIds?: string[];
      groupId?: string;
      subgroupId?: string;
      armed: boolean;
    }) => {
      const { data } = await positionOpenerAxiosApi.patch<{
        scope: 'all' | 'ids';
        matched: number;
        modified: number;
        skipped: number;
      }>('/rules/bulk-armed', dto);
      return data;
    },
    {
      mutationKey: 'entry:bulk-armed',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Hide a pair from the opener page. The pair stays
 * `matchedBy: 'manual'` and visible everywhere else (Opportunities,
 * Positions, Unwinding); only the opener filters it out of
 * `findManualConfirmed`. The PairsSyncService change-stream tick on
 * the backend then drops the Kalshi + Polymarket WS subscription via
 * the existing diff path. Optimistically removes the pair from the
 * cached `manual-confirmed-pairs` list so the row disappears the
 * moment the operator confirms — server invalidation lands the
 * canonical state right after.
 */
export function useHidePairFromOpener() {
  const qc = useQueryClient();
  return useMutation(
    async (pairId: string) => {
      const { data } = await predictionManagerAxiosApi.patch(
        `/pairs/${pairId}/hide-from-opener`
      );
      return data;
    },
    {
      onMutate: async (pairId) => {
        await qc.cancelQueries(['manual-confirmed-pairs', OPENER_PAIR_STATUS]);
        const prev = qc.getQueryData<ManualConfirmedPair[]>([
          'manual-confirmed-pairs',
          OPENER_PAIR_STATUS
        ]);
        if (Array.isArray(prev)) {
          qc.setQueryData<ManualConfirmedPair[]>(
            ['manual-confirmed-pairs', OPENER_PAIR_STATUS],
            prev.filter((p) => p._id !== pairId)
          );
        }
        return { prev };
      },
      onError: (_err, _pairId, ctx) => {
        // Rollback the optimistic removal on failure so the row
        // pops back rather than silently disappearing forever.
        if (ctx?.prev) {
          qc.setQueryData(
            ['manual-confirmed-pairs', OPENER_PAIR_STATUS],
            ctx.prev
          );
        }
      },
      onSettled: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

/**
 * Per-pair opener kill switch — flips `prediction_pairs.openerEnabled`.
 * Newly manual-confirmed pairs default to false; toggling to true is what
 * makes the WS subs start streaming and lets `RulesService.activate`
 * succeed. Toggling to false on the backend auto-pauses every active rule
 * on the pair and lets the pruner drop the WS books (~5 min). The Entry
 * page reads the live state from the `opening_state_update` envelope
 * (field `pairEnabled`), so this mutation does NOT optimistically patch
 * a separate cache — the WS bridge is the source of truth.
 */
export function useSetPairOpenerEnabled() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, enabled }: { pairId: string; enabled: boolean }) => {
      const { data } = await predictionManagerAxiosApi.patch(
        `/pairs/${pairId}/opener-enabled`,
        { enabled }
      );
      return data;
    },
    {
      // Refresh the pair list so `pair.openerEnabled` reflects the new value —
      // the maker-flag arm path reads it to decide whether to auto-enable the
      // pair first (see `needsOpenerAutoEnable`), so a stale `false` would make
      // it re-enable redundantly until the 30s poll.
      onSettled: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

/**
 * Bulk per-pair opener kill switch — flips `prediction_pairs.openerEnabled`
 * on many pairs in one round trip. `scope: 'ids'` flips the given pairIds;
 * `scope: 'all'` flips every opener-eligible pair server-side (so the UI
 * never has to ship hundreds of ids). Same cascade as the single-pair
 * `useSetPairOpenerEnabled` (auto-pause rules + WS prune within ~5 min);
 * live state still arrives via the `opening_state_update` envelope, so this
 * only invalidates the pair list to refresh row metadata.
 */
export function useBulkSetPairOpenerEnabled() {
  const qc = useQueryClient();
  return useMutation(
    async (vars: {
      pairIds?: string[];
      scope?: 'ids' | 'all';
      enabled: boolean;
      groupId?: string;
    }) => {
      const { data } = await predictionManagerAxiosApi.patch(
        '/pairs/bulk-opener-enabled',
        vars
      );
      return data as {
        scope: 'ids' | 'all';
        matched: number;
        modified: number;
      };
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

export function useActivateRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).post<EntryRule>(`/rules/${id}/activate`)
      );
      return data;
    },
    {
      // Tagged so the app-wide MutationCache.onError backstop surfaces a 409
      // (e.g. core refusing a grouped-pair activation) instead of failing
      // silently — the groups-only stem routing in mutationErrorHandler still
      // applies regardless of this key.
      mutationKey: 'entry:activate',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function usePauseRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).post<EntryRule>(`/rules/${id}/pause`)
      );
      return data;
    },
    {
      mutationKey: 'entry:pause',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Explicit arm/disarm intent (spec D5/O6). The operator's on/off gesture sends
 * a single explicit boolean — `PATCH /rules/{id}` with `{ desiredArmed }` — and
 * that boolean is the ONLY thing that arms or pauses the rule. The backend no
 * longer INFERS arming from a maker-flag `false→true` transition or from a
 * params `update`, so a stale/other-writer update can never silently re-arm a
 * rule the operator turned off. The maker flags stay pure per-leg config.
 *
 * Routed by `resolveOpenerApiForRule` (grouped → owning feed pod, fail-visible
 * if unresolvable; ungrouped → core), enqueued on the shared opener-write
 * budget, and tagged `entry:set-armed` so the app-wide `MutationCache.onError`
 * backstop surfaces a failure with tailored copy. PATCH (not a dedicated
 * endpoint) keeps the write on the same routed instance as every other per-rule
 * mutation.
 */
export function useSetRuleArmed() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, desiredArmed }: { id: string; desiredArmed: boolean }) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).patch<EntryRule>(`/rules/${id}`, {
          desiredArmed
        })
      );
      return data;
    },
    {
      mutationKey: 'entry:set-armed',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

/**
 * Toggle a rule's `allowThresholdAboveOne` flag — the per-rule opt-in that
 * lets an `always-threshold` rule's threshold rest above $1 (up to 1.4).
 * Mirrors `useActivateRule`: group-scoped client resolution, a `mutationKey`
 * so the app-wide `MutationCache.onError` backstop surfaces the 400 the
 * backend returns when disabling while the threshold is still ≥ 1, and an
 * `entry-rules` invalidate on success.
 */
export function useSetAllowThresholdAboveOne() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { data } = await enqueueOpenerWrite(() =>
        resolveOpenerApiForRule(qc, id).post<EntryRule>(
          `/rules/${id}/allow-threshold-above-one`,
          { enabled }
        )
      );
      return data;
    },
    {
      mutationKey: 'entry:allow-threshold-above-one',
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      }
    }
  );
}

export function useEntryRuleExecutions(ruleId: string | null) {
  return useQuery<EntryExecution[]>(
    ['entry-executions', ruleId],
    async () => {
      if (!ruleId) return [];
      const { data } = await positionOpenerAxiosApi.get<EntryExecution[]>(
        `/rules/${ruleId}/executions`
      );
      return data;
    },
    {
      enabled: !!ruleId,
      refetchInterval: 10000,
      keepPreviousData: true
    }
  );
}

export function useEntryOrders() {
  return useQuery<EntryOrdersResponse>(
    'entry-orders',
    async () => {
      const { data } = await positionOpenerAxiosApi.get<EntryOrdersResponse>(
        '/orders'
      );
      return data;
    },
    { refetchInterval: 3000, keepPreviousData: true }
  );
}

/**
 * Pair-manager response shape: each pair has `siteA` / `siteB` legs,
 * each tagged with a `platform`. siteA is *usually* Polymarket but the
 * adapter below treats platform as the source of truth so a future
 * matcher flip (Kalshi-first) doesn't silently misalign the legs.
 */
interface PairManagerSite {
  platform: 'polymarket' | 'kalshi';
  marketId?: string;
  title?: string;
  marketUrl?: string;
  yesTokenId?: string;
  noTokenId?: string;
  canonicalKey?: string;
}
export interface PairManagerPair {
  _id: string;
  name?: string;
  pairName?: string;
  status?: string;
  archived?: boolean;
  expiryDate?: string | null;
  category?: string | null;
  subcategory?: string | null;
  marketType?: string | null;
  openerGroupId?: string | null;
  openerSubgroupId?: string | null;
  openerEnabled?: boolean;
  siteA?: PairManagerSite;
  siteB?: PairManagerSite;
}

export function adaptPair(raw: PairManagerPair): ManualConfirmedPair {
  const sites = [raw.siteA, raw.siteB].filter(
    (s): s is PairManagerSite => !!s,
  );
  const polySite = sites.find(s => s.platform === 'polymarket');
  const kalshiSite = sites.find(s => s.platform === 'kalshi');
  return {
    _id: raw._id,
    pairName: raw.pairName ?? raw.name ?? '',
    polymarketTitle: polySite?.title,
    kalshiTitle: kalshiSite?.title,
    expiryDate: raw.expiryDate ?? null,
    category: raw.category ?? null,
    subcategory: raw.subcategory ?? null,
    marketType: raw.marketType ?? null,
    openerGroupId: raw.openerGroupId ?? null,
    openerSubgroupId: raw.openerSubgroupId ?? null,
    openerEnabled: raw.openerEnabled ?? false,
    status: raw.status,
    archived: raw.archived,
    polymarket: polySite
      ? {
          yesTokenId: polySite.yesTokenId,
          noTokenId: polySite.noTokenId,
          conditionId: polySite.marketId,
          marketUrl: polySite.marketUrl,
        }
      : null,
    kalshi: kalshiSite
      ? {
          ticker: kalshiSite.marketId,
          marketUrl: kalshiSite.marketUrl,
        }
      : null,
  };
}

/**
 * PANIC button — operator-initiated emergency stop on the opener:
 * batch-cancel every active maker buy and pause every entry rule. The
 * resulting cancellation events are surfaced as desired operator
 * cancellations (severity = warning, not error) so the in-app feed +
 * Slack distinguish them from engine faults.
 *
 * Resilience: fans out to BOTH the position-opener /rules/panic AND
 * prediction-manager /api/system/panic (which pauses every active entry
 * rule directly in Mongo + best-effort kicks the service again).
 * `Promise.allSettled` so one unreachable pod can never leave the system
 * armed — at least one of the two paths is enough to guarantee no new
 * orders. Duplicate panic on the service is idempotent.
 */
export function useEntryPanic() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const [serviceRes, managerRes] = await Promise.allSettled([
        positionOpenerAxiosApi.post('/rules/panic'),
        predictionManagerAxiosApi.post('/api/system/panic', { target: 'opener' }),
      ]);
      if (serviceRes.status === 'rejected' && managerRes.status === 'rejected') {
        throw new Error(
          `Both panic paths failed — service: ${serviceRes.reason}; manager: ${managerRes.reason}`,
        );
      }
      return {
        service:
          serviceRes.status === 'fulfilled' ? serviceRes.value.data : null,
        manager:
          managerRes.status === 'fulfilled' ? managerRes.value.data : null,
      };
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      },
    },
  );
}

/**
 * Soft panic (24h) for the opener — same resilient fan-out as
 * `useEntryPanic`, but cancels buys only on markets of entry rules active
 * in the last 24h. Orders on unrelated markets are left untouched.
 */
export function useEntryPanicRecent() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const [serviceRes, managerRes] = await Promise.allSettled([
        positionOpenerAxiosApi.post('/rules/panic-recent'),
        predictionManagerAxiosApi.post('/api/system/panic-recent', { target: 'opener' }),
      ]);
      if (serviceRes.status === 'rejected' && managerRes.status === 'rejected') {
        throw new Error(
          `Both soft-panic paths failed — service: ${serviceRes.reason}; manager: ${managerRes.reason}`,
        );
      }
      return {
        service:
          serviceRes.status === 'fulfilled' ? serviceRes.value.data : null,
        manager:
          managerRes.status === 'fulfilled' ? managerRes.value.data : null,
      };
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      },
    },
  );
}

export function useEntryResume() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await positionOpenerAxiosApi.post('/rules/resume');
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('entry-rules');
      },
    },
  );
}

/**
 * Stop/Start the position-opener deployment via prediction-manager. Mirrors
 * the unwinder controls: stop runs a precondition check + (optional) panic
 * + scale-to-0; start scales to 1 and waits for Ready. The state hook polls
 * every 3 s so the button flips promptly once the patch lands.
 */
export function useOpenerState() {
  return useQuery<OpenerState>(['opener-state'], getOpenerState, {
    refetchInterval: 3_000,
    keepPreviousData: true,
  });
}

export function useStopOpener() {
  const qc = useQueryClient();
  return useMutation(stopOpener, {
    onSuccess: job => {
      // Seed system/job so UpdatesProvider's stepper picks it up immediately.
      // EntryPage wraps the route in `<UpdatesProvider scope="predictions">`,
      // and that provider's job-poll query key is `['system/job', scope]` —
      // a string-only key like 'system/job' silently misses, leaving the
      // stepper popover invisible and the user with no feedback.
      qc.setQueryData(['system/job', 'predictions'], job);
      qc.invalidateQueries(['system/job', 'predictions']);
      qc.invalidateQueries('entry-rules');
      qc.invalidateQueries(['opener-state']);
    },
  });
}

export function useStartOpener() {
  const qc = useQueryClient();
  return useMutation(startOpener, {
    onSuccess: job => {
      qc.setQueryData(['system/job', 'predictions'], job);
      qc.invalidateQueries(['system/job', 'predictions']);
      qc.invalidateQueries(['opener-state']);
    },
  });
}

export function useClearOpenerPanicLockout() {
  const qc = useQueryClient();
  return useMutation(clearOpenerPanicLockout, {
    onSuccess: () => {
      // Re-fetch state so the lockout banner clears + START re-enables.
      qc.invalidateQueries(['opener-state']);
    },
  });
}

/**
 * Manually-confirmed prediction pairs that are eligible for entry rules.
 * Lives on the pair-manager service (NOT position-opener) — re-uses the
 * existing `predictionManagerAxiosApi`. Refetches every 30 s so newly
 * confirmed pairs show up without a hard reload.
 *
 * The pair-manager returns the raw matcher schema (`siteA`/`siteB`/`name`);
 * `adaptPair` flattens that into the frontend's `ManualConfirmedPair`
 * interface so the row component doesn't need to know about the storage
 * shape.
 */
export function useManualConfirmedPairs() {
  return useQuery<ManualConfirmedPair[]>(
    ['manual-confirmed-pairs', OPENER_PAIR_STATUS],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<PairManagerPair[]>(
        '/pairs/manual-confirmed',
        { params: { status: OPENER_PAIR_STATUS } },
      );
      return Array.isArray(data) ? data.map(adaptPair) : [];
    },
    { refetchInterval: 30_000, keepPreviousData: true }
  );
}
